import { useEffect, useState } from 'react';
import { useOrgUnit, useCreateOrgUnit, useUpdateOrgUnit } from '../../../hooks/useOrgUnits';
import { useSites } from '../../../hooks/useSites';
import type {
  OrgUnit,
  OrgUnitSelection,
  OrgUnitType,
  UpdateOrgUnitInput,
} from '../../../types/orgUnit';

interface Props {
  selection: OrgUnitSelection;
  orgUnits: OrgUnit[];
  onClearSelection: () => void;
  onBindSites: (orgUnitId: string) => void;
  onDelete: (orgUnitId: string) => void;
}

interface FieldErrors {
  name?: string;
  parent_id?: string;
  banner?: string;
}

function classifyError(msg: string): keyof FieldErrors {
  const m = msg.toLowerCase();
  if (m.includes('parent') || m.includes('cycle')) return 'parent_id';
  if (m.includes('name')) return 'name';
  return 'banner';
}

function capitalize(s: string): string {
  return s.length === 0 ? s : s[0].toUpperCase() + s.slice(1);
}

export function OrgUnitDetail({
  selection,
  orgUnits,
  onClearSelection,
  onBindSites,
  onDelete,
}: Props) {
  const detailQuery = useOrgUnit(selection.kind === 'unit' ? selection.id : null);
  const createMut = useCreateOrgUnit();
  const updateMut = useUpdateOrgUnit();
  const sitesQuery = useSites({ limit: 200 });

  const [name, setName] = useState('');
  const [parentId, setParentId] = useState<string | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    setErrors({});
    setMessage(null);
    if (selection.kind === 'unit' && detailQuery.data) {
      setName(detailQuery.data.name);
      setParentId(detailQuery.data.parent_id);
    } else if (selection.kind === 'create') {
      setName('');
      setParentId(selection.parent_id ?? null);
    } else {
      setName('');
      setParentId(null);
    }
  }, [selection, detailQuery.data]);

  if (selection.kind === 'none') {
    return (
      <div className="flex items-center justify-center h-full text-ems-text-muted text-sm">
        Select a unit from the tree, or create your first region.
      </div>
    );
  }

  const isEdit = selection.kind === 'unit';
  const detail = detailQuery.data;

  if (isEdit && !detail) {
    return (
      <div className="flex items-center justify-center h-full text-ems-text-muted text-sm">
        Loading…
      </div>
    );
  }
  const currentType: OrgUnitType =
    selection.kind === 'create' ? selection.type : detail?.type ?? 'region';

  const isDirty =
    isEdit && detail
      ? name.trim() !== detail.name || parentId !== detail.parent_id
      : selection.kind === 'create'
      ? name.trim().length > 0
      : false;

  const boundSites =
    isEdit && detail && currentType === 'district'
      ? (sitesQuery.data?.sites ?? []).filter(
          (s: any) => s.org_unit_id === detail.org_unit_id,
        )
      : [];

  async function handleSave() {
    setErrors({});
    setMessage(null);
    try {
      if (selection.kind === 'create') {
        await createMut.mutateAsync({
          type: selection.type,
          name: name.trim(),
          parent_id: selection.parent_id ?? undefined,
        });
        setMessage('Created');
        onClearSelection();
      } else if (selection.kind === 'unit' && detail) {
        const patch: UpdateOrgUnitInput = {};
        if (name.trim() !== detail.name) patch.name = name.trim();
        if (parentId !== detail.parent_id) patch.parent_id = parentId;
        await updateMut.mutateAsync({ id: detail.org_unit_id, patch });
        setMessage('Saved');
      }
    } catch (err: any) {
      const apiError = err?.response?.data?.error ?? 'Unknown error';
      const bucket = classifyError(apiError);
      setErrors({ [bucket]: apiError });
    }
  }

  function handleCancel() {
    if (selection.kind === 'unit' && detail) {
      setName(detail.name);
      setParentId(detail.parent_id);
      setErrors({});
    } else {
      onClearSelection();
    }
  }

  const regionOptions = orgUnits.filter((u) => u.type === 'region' && !u.deleted_at);
  const organizationOptions = orgUnits.filter(
    (u) => u.type === 'organization' && !u.deleted_at,
  );

  return (
    <div className="p-4 overflow-y-auto">
      {message && (
        <div className="mb-3 px-3 py-2 rounded text-xs bg-ems-green/15 text-ems-green">
          {message}
        </div>
      )}
      {errors.banner && (
        <div className="mb-3 px-3 py-2 rounded text-xs bg-ems-red/15 text-ems-red">
          {errors.banner}
        </div>
      )}

      <div className="flex justify-between items-center mb-3">
        <div className="text-ems-text-primary font-semibold">
          {isEdit
            ? `${capitalize(currentType)}: ${detail?.name ?? ''}`
            : `New ${selection.kind === 'create' ? selection.type : ''}`}
        </div>
        <div className="flex gap-2">
          {isDirty && (
            <button
              type="button"
              onClick={handleCancel}
              className="px-3 py-1.5 rounded text-xs border border-ems-border text-ems-text-secondary hover:bg-ems-forest/40"
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            disabled={!isDirty || createMut.isPending || updateMut.isPending}
            onClick={handleSave}
            className="px-3 py-1.5 rounded text-xs bg-ems-forest text-ems-green border border-ems-green/20 disabled:opacity-40 hover:bg-ems-forest/80"
          >
            {createMut.isPending || updateMut.isPending
              ? 'Saving…'
              : selection.kind === 'create'
              ? 'Create'
              : 'Save'}
          </button>
          {isEdit && detail && (
            <button
              type="button"
              onClick={() => onDelete(detail.org_unit_id)}
              className="px-3 py-1.5 rounded text-xs border border-ems-red/50 text-ems-red hover:bg-ems-red/10"
            >
              Delete…
            </button>
          )}
        </div>
      </div>

      <label className="block text-xs text-ems-text-secondary mb-1">Name</label>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Name"
        className="w-full px-2 py-1.5 rounded bg-ems-bg-card border border-ems-border text-sm text-ems-text-primary"
      />
      {errors.name && (
        <div className="mt-1 text-xs text-ems-red">{errors.name}</div>
      )}

      {currentType === 'district' && (
        <>
          <label className="block text-xs text-ems-text-secondary mt-3 mb-1">
            Parent region
          </label>
          <select
            value={parentId ?? ''}
            onChange={(e) => setParentId(e.target.value || null)}
            className="w-full px-2 py-1.5 rounded bg-ems-bg-card border border-ems-border text-sm text-ems-text-primary"
          >
            <option value="">— none —</option>
            {regionOptions.map((r) => (
              <option key={r.org_unit_id} value={r.org_unit_id}>
                {`${r.name} (region)`}
              </option>
            ))}
          </select>
          {errors.parent_id && (
            <div className="mt-1 text-xs text-ems-red">{errors.parent_id}</div>
          )}
        </>
      )}

      {currentType === 'region' && organizationOptions.length > 0 && (
        <>
          <label className="block text-xs text-ems-text-secondary mt-3 mb-1">
            Parent organization (optional)
          </label>
          <select
            value={parentId ?? ''}
            onChange={(e) => setParentId(e.target.value || null)}
            className="w-full px-2 py-1.5 rounded bg-ems-bg-card border border-ems-border text-sm text-ems-text-primary"
          >
            <option value="">— root (no organization parent) —</option>
            {organizationOptions.map((o) => (
              <option key={o.org_unit_id} value={o.org_unit_id}>
                {`${o.name} (organization)`}
              </option>
            ))}
          </select>
          {errors.parent_id && (
            <div className="mt-1 text-xs text-ems-red">{errors.parent_id}</div>
          )}
        </>
      )}

      {isEdit && detail && currentType === 'district' && (
        <div className="mt-6">
          <div className="flex justify-between items-center mb-2">
            <div className="text-xs text-ems-text-secondary">
              Bound sites ({boundSites.length})
            </div>
            <button
              type="button"
              onClick={() => onBindSites(detail.org_unit_id)}
              className="text-xs text-ems-green hover:text-ems-green-bright"
            >
              + Bind sites
            </button>
          </div>
          {boundSites.length === 0 ? (
            <div className="text-xs text-ems-text-muted italic">No sites bound</div>
          ) : (
            <ul className="space-y-1">
              {boundSites.map((s: any) => (
                <li
                  key={s.site_id}
                  className="text-xs px-2 py-1 rounded bg-ems-bg-card border border-ems-border text-ems-text-primary"
                >
                  {s.name}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {isEdit && detail && (
        <div className="mt-6 pt-4 border-t border-ems-border text-xs text-ems-text-muted">
          <div>Children: {detail.children_count}</div>
          <div>Members: {detail.member_count}</div>
          <div>Sites: {detail.site_count}</div>
        </div>
      )}
    </div>
  );
}
